// KPI-ASSIGN: the KPIs tab content, one self-contained component for every place a seat is shown
// (the seat page, the DE profile's KPIs tab — AGENT-MENTION's regrouped profile drops it into its
// "KPIs" area as is — and anywhere else a seat id is known). It answers "what is this seat
// measured on, and how is it doing": "KPIs for this seat (held by Priya Shah)", so the
// seat-vs-person model is said where it matters; then one row per measurable of the stored set
// (Off rows too): its health this week (hit / missed / no data; context; off), the recent values
// with a sparkline, the target, where the numbers come from (manual / import / events / agent
// events / formula) and how it is scored. Managers add, edit and remove KPIs with the shared
// KPI assignment editor (kpi-assign-dialog.tsx); everyone else reads.
// On load: the seat's stored set, its scorecard for the week, every metric and the seat's target
// rules (four `kpi` reads; mount it behind `useFeature("kpi")`). Each row with a reporting duty
// (or a value pending review) carries AGENT-REPORTING's status chip, read off the scorecard row.
import {
  Badge,
  Button,
  EmptyState,
  ScoreMark,
  Sparkline,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useReturnFocus,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { MetricDef, ScorecardMetric, SeatMetric, TargetRule } from "../../api/types";
import { weekRangeLabel } from "../../focus/weeks";
import { type CompanyAccess, isManager } from "../company";
import { formatMetricValue, formatTarget } from "../format";
import { hitMiss } from "../hit-miss";
import { companyQuery, seatScorecardQuery } from "../queries";
import { QuickAddPreparing } from "../quick-add/preparing";
import { ReportingStatusChip, reportingProps } from "../reporting/reporting-status";
import { Loading } from "../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { ErrorNotice, FormError } from "../structure/parts";
import { describeQuery } from "./event-query";
import { KpiAssignDialog, type KpiAssignTarget, sourceText } from "./kpi-assign-dialog";
import { kpiKeys, seatSetQuery, seatTargetsQuery, useAllMetrics } from "./queries";

export type SeatHolder = { name: string; kind: "person" | "digital_employee" };

/** A chart seat's holders (`GET org-chart`) in the panel's terms. */
export function holdersOf(seat: {
  holders: readonly { display_name: string; membership_kind: string }[];
}): SeatHolder[] {
  return seat.holders.map((h) => ({
    name: workerName(h.display_name),
    kind: h.membership_kind === "digital_employee" ? "digital_employee" : "person",
  }));
}

/** "held by Priya Shah" / "held by Riley (digital employee)" / null (an open seat). */
export function holderText(holders: readonly SeatHolder[]): string | null {
  if (!holders.length) return null;
  const names = holders.map((h) =>
    h.kind === "digital_employee" ? `${h.name} (digital employee)` : h.name,
  );
  return `held by ${names.join(" and ")}`;
}

export function SeatKpisPanel({
  api,
  company,
  seatId,
  seatTitle,
  holders,
  week,
  readOnly = false,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  seatTitle: string;
  /** The seat's current holders, for the label (a seat may hold a person and a DE at once). */
  holders: readonly SeatHolder[];
  /** The focused ISO week, e.g. 2026-W43. */
  week: string;
  /** A closed seat, or a caller that only shows. Managers otherwise edit. */
  readOnly?: boolean;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const manager = isManager(company) && !readOnly;
  const set = useQuery(seatSetQuery(api, co, seatId));
  const card = useQuery(seatScorecardQuery(api, co, seatId, week));
  const targets = useQuery(seatTargetsQuery(api, co, seatId));
  const metrics = useAllMetrics(api, co);
  const detail = useQuery(companyQuery(api, co));
  const currency = detail.data?.reporting_currency ?? card.data?.cost?.currency ?? "USD";
  const [dialog, setDialog] = useState<"add" | { row: SeatMetric } | null>(null);
  const remove = useMutation({
    // Under the version this list was drawn from: a set changed since is a 409, then reloaded.
    mutationFn: (metricId: string) =>
      api.client.seats.removeMetric({
        params: { co, id: seatId, metric_id: metricId },
        ifMatch: set.data?.version ?? 0,
      }),
    onSuccess: (saved) => {
      qc.setQueryData(kpiKeys.seatSet(co, seatId), saved);
      void qc.invalidateQueries({
        queryKey: ["company", co],
        predicate: (q) => q.queryKey[4] !== "metric-set",
      });
    },
    onError: () => void qc.invalidateQueries({ queryKey: kpiKeys.seatSet(co, seatId) }),
  });

  const held = holderText(holders);
  const agent = holders.some((h) => h.kind === "digital_employee");
  const target: KpiAssignTarget = { seatId, seatTitle, holderText: held, agent };
  const rows = set.data?.data ?? [];
  const defs = new Map(metrics.all.map((m) => [m.id, m]));
  const onCard = new Map((card.data?.metrics ?? []).map((m) => [m.metric_def_id, m]));
  const rulesOf = (id: string) =>
    (targets.data?.data ?? [])
      .filter((t) => t.metric_def_id === id && t.effective_to === null)
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from));

  return (
    <section
      className="flex min-w-0 flex-col gap-3"
      aria-labelledby={`seat-kpis-${seatId}`}
      data-slot="seat-kpis"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 id={`seat-kpis-${seatId}`} className="text-base font-semibold">
            KPIs for this seat{held ? ` (${held})` : ""}
          </h2>
          <p className="text-xs text-muted-foreground">
            {seatTitle} · week {week} ({weekRangeLabel(week)}). KPIs belong to the seat: a new
            holder carries them, and their history stays here.
          </p>
        </div>
        {manager ? (
          <Button size="sm" onClick={() => setDialog("add")} data-slot="add-kpi">
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            Add KPI
          </Button>
        ) : null}
      </div>

      {set.isPending || card.isPending ? (
        <Loading label="Loading the seat's KPIs" rows={4} />
      ) : set.isError ? (
        <ErrorNotice what="The seat's KPIs" error={set.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="card"
          title="No KPIs on this seat yet"
          description={
            manager
              ? "Add a KPI: pick or create a metric, set its target and how it is scored, in one step."
              : "A manager adds the seat's KPIs."
          }
          actions={
            manager ? (
              <Button size="sm" onClick={() => setDialog("add")}>
                <PlusIcon data-icon="inline-start" aria-hidden="true" />
                Add KPI
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Table className={DENSE_TABLE} data-slot="seat-kpi-table">
          <TableCaption className="sr-only">
            The seat's KPIs: health this week, recent values, target, source and scoring.
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">KPI</TableHead>
              <TableHead scope="col">Health</TableHead>
              <TableHead scope="col">Recent</TableHead>
              <TableHead scope="col" className="text-right">
                Target
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Source
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Scoring
              </TableHead>
              {manager ? (
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const def = defs.get(r.metric_def_id);
              const m = onCard.get(r.metric_def_id);
              const name = def?.name ?? m?.name ?? "…";
              return (
                <TableRow
                  key={r.metric_def_id}
                  className="group/row"
                  data-kpi={def?.key ?? r.metric_def_id}
                >
                  <TableHead scope="row" className="max-w-56 font-normal">
                    <span className="flex flex-col">
                      <span className="truncate font-medium">{name}</span>
                      {def?.event_query ? (
                        <span className="truncate text-xs text-muted-foreground">
                          {describeQuery(def.event_query)}
                        </span>
                      ) : null}
                      {m?.reporting || m?.pending_review ? (
                        <span className="mt-0.5" data-field="reporting">
                          <ReportingStatusChip compact {...reportingProps(m)} />
                        </span>
                      ) : null}
                    </span>
                  </TableHead>
                  <TableCell data-field="health">
                    <Health row={r} m={m} currency={currency} />
                  </TableCell>
                  <TableCell data-field="recent">
                    <Recent m={m} currency={currency} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-field="target">
                    {targetText(m, def, rulesOf(r.metric_def_id)[0], currency)}
                  </TableCell>
                  <TableCell className="max-md:hidden" data-field="source">
                    {def ? <Badge variant="outline">{sourceText(def, agent)}</Badge> : "—"}
                  </TableCell>
                  <TableCell className="max-md:hidden" data-field="scoring">
                    <Scoring row={r} />
                  </TableCell>
                  {manager ? (
                    <TableCell className="w-px">
                      <RowActions>
                        <RowAction
                          icon={PencilIcon}
                          label={`Edit ${name}`}
                          tip="Edit target, weight and scoring"
                          onClick={() => setDialog({ row: r })}
                        />
                        <RowAction
                          icon={Trash2Icon}
                          label={`Remove ${name} from this seat`}
                          tip="Remove from this seat (its history stays)"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(r.metric_def_id)}
                        />
                      </RowActions>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <FormError what="Removing the KPI" error={remove.error} />
      {card.isError ? <ErrorNotice what="This week's scorecard" error={card.error} /> : null}

      {dialog ? (
        <KpiAssignDialog
          api={api}
          company={company}
          seat={target}
          onSeat={rows}
          {...(dialog === "add"
            ? {}
            : {
                editing: { row: dialog.row, target: rulesOf(dialog.row.metric_def_id)[0] ?? null },
              })}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}

/**
 * The KPI editor on its own, for a caller that has no KPIs panel on screen (the chart panel's
 * "Add a measurable"): it reads the seat's stored set first, so a metric on it is not offered.
 */
export function AddKpiDialog({
  api,
  company,
  seatId,
  seatTitle,
  holders,
  holder,
  asGoal = false,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  seatTitle: string;
  /** The chart's holders of the seat; or `holder`, when the caller has them otherwise. */
  holders?: readonly SeatHolder[];
  holder?: { text: string; agent: boolean };
  /** SEAT-DRAWER-UX: added as a goal (goal weight 1, KPI weight 0). */
  asGoal?: boolean;
  onClose: () => void;
}) {
  const set = useQuery(seatSetQuery(api, company.id, seatId));
  // MODALS: focus goes back to the button that opened the editor, across the loading state.
  useReturnFocus();
  if (!set.data)
    return (
      <QuickAddPreparing
        title="Add a KPI"
        failed={set.isError}
        retry={() => void set.refetch()}
        onClose={onClose}
      />
    );
  return (
    <KpiAssignDialog
      api={api}
      company={company}
      seat={{
        seatId,
        seatTitle,
        holderText: holder?.text ?? holderText(holders ?? []),
        agent: holder?.agent ?? (holders ?? []).some((h) => h.kind === "digital_employee"),
      }}
      onSeat={set.data.data}
      asGoal={asGoal}
      onClose={onClose}
      onSaved={() => toast.add({ title: asGoal ? "Goal saved" : "KPI saved", type: "success" })}
    />
  );
}

function Health({
  row,
  m,
  currency,
}: {
  row: SeatMetric;
  m: ScorecardMetric | undefined;
  currency: string;
}) {
  if (row.tracking_mode === "off")
    return <span className="text-xs text-muted-foreground">Off · not scored</span>;
  if (!m) return <ScoreMark state="none" showLabel />;
  if (row.tracking_mode === "context")
    return (
      <span className="text-xs text-muted-foreground">
        Context · {formatMetricValue(m.actual, m.unit, currency)}
      </span>
    );
  const state = hitMiss(m.direction, m.actual, m.target, m.target_max);
  const actual = formatMetricValue(m.actual, m.unit, currency);
  const label =
    state === "none"
      ? m.target === null && m.actual !== null
        ? `${actual} · no target`
        : "No data yet"
      : `${state === "hit" ? "Hit" : "Missed"} · ${actual}`;
  return <ScoreMark state={state} label={label} showLabel />;
}

function Recent({ m, currency }: { m: ScorecardMetric | undefined; currency: string }) {
  if (!m?.trend.length) return <span className="text-xs text-muted-foreground">—</span>;
  // The last three periods: four overflowed a DE's table at 1440 (money values are wide).
  const last = m.trend.slice(-3);
  const text = last
    .map((p) => (p.actual === null ? "—" : formatMetricValue(p.actual, m.unit, currency)))
    .join(" · ");
  return (
    <span className="flex items-center gap-2">
      <Sparkline
        label={`${m.name}, last ${m.trend.length} weeks: ${m.trend
          .map((p) => (p.actual === null ? "no data" : String(p.actual)))
          .join(", ")}`}
        values={m.trend.map((p) => p.actual)}
        reference={m.trend.map((p) => p.target)}
      />
      <span className="text-xs whitespace-nowrap text-muted-foreground tabular-nums">{text}</span>
    </span>
  );
}

function targetText(
  m: ScorecardMetric | undefined,
  def: MetricDef | undefined,
  rule: TargetRule | undefined,
  currency: string,
): string {
  if (m && m.target !== null) return formatTarget(m, currency);
  if (rule && def) {
    const v = formatMetricValue(Number(rule.value), def.unit, currency);
    return `${v} / ${rule.period_type}`;
  }
  return "—";
}

function Scoring({ row }: { row: SeatMetric }) {
  if (row.tracking_mode === "off") return <Badge variant="neutral">Off</Badge>;
  if (row.tracking_mode === "context") return <Badge variant="neutral">Context</Badge>;
  return (
    <span className="flex flex-wrap items-center gap-1 text-xs">
      <span className="tabular-nums">Weight {Number(row.kpi_weight)}</span>
      {row.penalty_only ? <Badge variant="warning">Penalty only</Badge> : null}
    </span>
  );
}
